import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import FormHeader from 'components/FormHeader';
import { FormRow, UploadHint } from 'styles/CommonStyles';
import FormTextInput from 'components/FormTextInput';
import FormFileUpload from 'components/FormFileUpload';
import FormFormattedInput from 'components/FormFormattedInput';
import FormSubmit from 'components/FormSubmit';
import FormTracker from 'components/FormTracker';
import useSubmitActions from 'hooks/submit';
import useUploadActions from 'hooks/upload';
import { acceptedFiles, FormIdentifier } from 'lib/constants';
import { normalizePhoneNumber } from 'lib/utils';
import { patterns } from 'lib/validation';
import { showSubmitSuccessState } from 'state/atoms/submit';

const DeceasedForm = () => {
  const { i18n, t } = useTranslation();
  const { submitForm } = useSubmitActions();
  const { uploadFiles } = useUploadActions();
  const formMethods = useForm();
  const setShowSuccess = useSetRecoilState(showSubmitSuccessState);

  const onSubmit = useCallback(
    (data: any) => {
      const { documents, phoneNumber, ...otherData } = data;
      const formData = {
        formType: FormIdentifier.DECEASED,
        phoneNumber: normalizePhoneNumber(phoneNumber),
        ...otherData,
      };
      const formSessionUuid = uuidv4();

      return uploadFiles(formSessionUuid, 'servicing', documents)
        .then((uploadedDocs) =>
          submitForm(
            formSessionUuid,
            { ...formData, documents: uploadedDocs },
            i18n.language
          )
        )
        .then(() => setShowSuccess(true));
    },
    [i18n.language, setShowSuccess, submitForm, uploadFiles]
  );

  return (
    <FormProvider {...formMethods}>
      <FormHeader
        description={t('forms.deceased.description')}
        title={t('forms.deceased.name')}
      />
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.name')}
            name="name"
            rules={{
              pattern: {
                value: patterns.noSpecialCharacters,
                message: t<string>('validation.invalid'),
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.phoneNumber')}
            name="phoneNumber"
            rules={{
              pattern: {
                message: t<string>('validation.phoneNumberFormat'),
                value: patterns.phoneNumber,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <FormFormattedInput
            label={t<string>('labels.ssnDigits')}
            maxLength={4}
            name="stableId"
            obscurable
            placeholder="&bull;&bull;&bull;&bull;"
            rules={{
              required: t<string>('validation.required'),
              pattern: {
                message: t<string>('validation.ssnDigitsFormat'),
                value: patterns.lastFourDigits,
              },
            }}
          />
          <FormTextInput
            label={t<string>('labels.emailAddressRequester')}
            name="email"
            rules={{
              pattern: {
                message: t<string>('validation.emailFormat'),
                value: patterns.emailAddress,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <div>
            <FormFileUpload
              acceptTypes={acceptedFiles}
              ariaLabel={t('labels.uploadDocuments')}
              label={
                <>
                  {t('labels.uploadDocuments')}
                  <UploadHint>{t('labels.uploadDeathCertificate')}</UploadHint>
                </>
              }
              maxFiles={1}
              name="documents"
              rules={{
                required: t<string>('validation.uploadDocumentsRequired'),
              }}
            />
          </div>
        </FormRow>
        <FormSubmit formType={FormIdentifier.DECEASED} />
        <FormTracker formType={FormIdentifier.DECEASED} />
      </form>
    </FormProvider>
  );
};

export default DeceasedForm;
